import { brands } from 'noddi-types';
import { cn } from 'noddi-ui-common';
import { customerRoutes } from 'noddi-util';
import { Outlet, useLocation } from 'react-router-dom';
import { useCurrentBrand } from '../../stores/BrandStore/brandStore';
import { NavioContactChatWidget } from '../BookingFlow/Common/NavioContactChatWidget';
import { CustomerLoginNoticeBanner, CustomerLoginNoticeTopBanner } from '../Common/CustomerLoginNoticeBanner';
import { CustomerNoticeTopBanner } from '../Common/CustomerNoticeTopBanner';
import { NoticeBanner } from '../Common/NoticeBanner';
import { CustomerHeaderNav } from './CustomerHeaderNav';
import { PageLayoutProvider } from './PageLayoutProvider';
import { MainContent, SkipToMainContent } from './SkipToMainContent';
import { usePageBackground } from './usePageBackground';
import { usePageWidth } from './usePageWidth';

const RouterLayoutInner = ({ className, showChat }: { className?: string; showChat?: boolean }) => {
  const { pathname } = useLocation();
  const { currentBrand } = useCurrentBrand();
  const pageWidth = usePageWidth();
  const isWidePage = pageWidth === 'wide';
  const background = usePageBackground();

  const isLoginPage = pathname === customerRoutes.login.getPath();
  const isBuildingBooking = pathname.includes(customerRoutes.newBookingBuilding.getBasePath());
  const isBuildingProposal = pathname.includes(customerRoutes.bookingProposalBuilding.getBasePath());
  const isBookingSlugPage = pathname.includes('/b/') && !pathname.includes('/offer');

  const hideChat =
    // Campaign claim pages anchor their CTA to the bottom on mobile; the bubble would sit on top of it.
    pathname.startsWith('/campaigns/') ||
    // Every tire offer step renders the chat in its own bottom bar instead.
    pathname.includes('tire-offer/info/') ||
    pathname.includes('tire-offer/select/') ||
    pathname.includes('tire-offer/address/') ||
    pathname.includes('tire-offer/login/') ||
    pathname.includes('tire-offer/summary/') ||
    // A short form with its own step actions at the bottom; the bubble only competes with them.
    pathname.startsWith(customerRoutes.requestTireOffer.getBasePath()) ||
    pathname.includes('delivery-map');

  const showChatInScreen = (isBuildingBooking || isBuildingProposal || showChat) && !hideChat;

  const useLightGradient = isBookingSlugPage && currentBrand === brands.noddi;
  const backgroundClass = (() => {
    if (background === 'grouped') {
      return 'bg-grouped-background';
    }
    if (isWidePage) {
      return 'bg-tertiary-background';
    }
    return useLightGradient
      ? 'bg-pinto-to-white-gradient md:bg-primary-background-gradient'
      : 'bg-primary-background-gradient';
  })();

  return (
    <div className={cn('min-h-screen', backgroundClass, className)}>
      <SkipToMainContent />
      {isLoginPage ? <CustomerLoginNoticeTopBanner /> : <CustomerNoticeTopBanner />}
      <CustomerHeaderNav />
      <MainContent>
        {isLoginPage ? <CustomerLoginNoticeBanner /> : <NoticeBanner />}
        <Outlet />
      </MainContent>
      {showChatInScreen && (
        <div className='fixed right-10 bottom-10 z-docked'>
          <NavioContactChatWidget size='small' />
        </div>
      )}
    </div>
  );
};

export const RouterLayout = ({ className, showChat }: { className?: string; showChat?: boolean }) => (
  <PageLayoutProvider>
    <RouterLayoutInner className={className} showChat={showChat} />
  </PageLayoutProvider>
);
